import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpsertAdministratorRequestBody } from '@/src/apis/rest/administrators.dtos';
import {
  getAdministratorByIdAPI,
  putAdministratorsAPI,
} from '@/src/apis/rest/administrators';
import RadioGroup from '@/components/atom/RadioGroup';
import {
  DEFAULT_VALUES,
  formFields,
  formSchema,
} from '@/src/pages/Administrator/upsertField';
import { useEffect, useMemo, useRef } from 'react';
import { formatter } from '@/src/utils/formatter';
import dayjs from 'dayjs';
import { usePrompt } from '@/hooks/usePrompt';
import { pick } from 'lodash';
import { OPTIONS_CONST } from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';

const BACK_PATH = PATH.ADMINISTRATOR_DETAIL;

function AdministratorEditPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();

  const form = useForm<UpsertAdministratorRequestBody>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
  });
  const watchedFields = useWatch({
    control: form.control,
  });

  // mutation
  const putAdministrator = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: UpsertAdministratorRequestBody;
    }) => putAdministratorsAPI(id, params),
  });

  // state
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  const {
    data: administratorData,
    isFetched,
    isError,
  } = useQuery({
    queryKey: ['administrator', id],
    queryFn: () => getAdministratorByIdAPI(Number(id)),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
      },
    },
  });

  const isFormChanged = useMemo(() => {
    const fields = ['name', 'phone', 'department', 'isActivated'];

    const originData = JSON.stringify(pick(administratorData, ...fields));
    const formData = JSON.stringify(pick(watchedFields, ...fields));

    return originData !== formData && !form.formState.isSubmitted;
  }, [watchedFields, administratorData, form.formState.isSubmitted]);

  // event
  const handleSubmit = (param: UpsertAdministratorRequestBody) => {
    putAdministrator.mutate(
      {
        id: formatter.STRING_TO_NUM(id!),
        params: param,
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (isFetched && !isError && administratorData) {
      form.setValue(formFields.aId.name, administratorData.aId);
      form.setValue(formFields.name.name, administratorData.name);
      form.setValue(formFields.department.name, administratorData.department);
      form.setValue(formFields.phone.name, administratorData.phone);
      form.setValue(
        formFields.isActivated.name,
        administratorData.account.isActivated,
      );
      form.setValue(
        formFields.createdAt.name,
        dayjs(administratorData.createdAt).format('YYYY-MM-DD HH:mm:ss'),
      );
    }
  }, [isFetched, isError, administratorData, form]);

  // usePrompt
  usePrompt(isFormChanged);

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(BACK_PATH)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            type="button"
            onClick={openEditDialog}
            className="w-40"
            buttonSize="medium"
            disabled={!form.formState.isValid || !isFormChanged}
            isLoading={putAdministrator.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={['관리자 관리', '관리자 수정']}
        pageTitle="관리자 수정"
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="관리자 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6">
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name={formFields.aId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.aId.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder={formFields.aId.placeholder}
                          onClear={() => field.onChange('')}
                          {...field}
                          disabled
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.name.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.name.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder={formFields.name.placeholder}
                          onClear={() => field.onChange('')}
                          maxLength={20}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.department.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.department.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder={formFields.department.placeholder}
                          onClear={() => field.onChange('')}
                          maxLength={20}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name={formFields.phone.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.phone.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder={formFields.phone.placeholder}
                          onClear={() => field.onChange('')}
                          maxLength={13}
                          {...field}
                          onBlur={() => {
                            form?.trigger('phone');
                            field?.onBlur();
                          }}
                          onChange={e => {
                            field.onChange(
                              formatter.NUM_HYPHEN_INSTANTLY(e.target.value),
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.isActivated.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.isActivated.label}
                        isRequired
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.activated, value: 'true' },
                            {
                              label: OPTIONS_CONST.notActivated,
                              value: 'false',
                            },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.createdAt.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label={formFields.createdAt.label}
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder="-"
                          onClear={() => field.onChange('')}
                          {...field}
                          disabled
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
            </div>
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* 모달 */}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putAdministrator.isPending}
        />
      )}
    </>
  );
}

export default AdministratorEditPage;
